import { createBrowserRouter } from 'react-router';
import { reducedMotion } from './kit/motion';
import { routes } from './routes';

export const router = createBrowserRouter(routes);

// Page changes run inside a View Transition tagged `dr-vt-route` (see theme/devrobot.css): the old page
// softly lifts away while the new one settles in. Only real page changes animate; query-only updates
// (tabs, filters) and history jumps stay instant.
if (typeof document !== 'undefined' && 'startViewTransition' in document) {
  const navigate = router.navigate.bind(router);
  let routeChange = false;
  let forget: ReturnType<typeof setTimeout> | undefined;

  router.navigate = ((to: Parameters<typeof navigate>[0], opts?: Parameters<typeof navigate>[1]) => {
    if (typeof to === 'number' || to === null || reducedMotion() || !changesPage(to)) return navigate(to as never, opts as never);
    routeChange = true;
    // React Router starts the transition from an effect once the new page is ready (after navigate() resolves);
    // if it never does (a blocked or failed navigation), forget the flag so a later list or theme transition isn't tagged.
    clearTimeout(forget);
    forget = setTimeout(() => (routeChange = false), 1500);
    return navigate(to, { ...opts, viewTransition: opts?.viewTransition ?? true });
  }) as typeof router.navigate;

  const start = document.startViewTransition.bind(document);
  document.startViewTransition = ((arg: Parameters<typeof start>[0]) => {
    if (!routeChange) return start(arg);
    routeChange = false;
    clearTimeout(forget);
    const root = document.documentElement;
    root.classList.add('dr-vt-route');
    const vt = start(arg);
    void vt.finished.catch(() => {}).finally(() => root.classList.remove('dr-vt-route'));
    return vt;
  }) as typeof document.startViewTransition;
}

function changesPage(to: string | { pathname?: string }): boolean {
  const pathname = typeof to === 'string' ? to.split(/[?#]/)[0] : to.pathname;
  if (!pathname) return false;
  const next = new URL(pathname, window.location.href).pathname.replace(/\/+$/, '') || '/';
  const current = window.location.pathname.replace(/\/+$/, '') || '/';
  return next !== current;
}
